import React, { useState } from 'react';
import { ArrowLeft, MoreHorizontal, Plus, Users, Clock, Download, MessageSquare, CheckSquare } from 'lucide-react';
import { Button } from '../ui/Button';
import { Avatar } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { InsightCard } from './InsightCard';
import { ActivityFeed } from './ActivityFeed';
import { Workspace } from '../../types/workspace';
import { mockInsights } from '../../data/mockData';
interface WorkspaceDetailProps {
  workspace: Workspace;
  onBack: () => void;
}
export function WorkspaceDetail({
  workspace,
  onBack
}: WorkspaceDetailProps) {
  const [activeTab, setActiveTab] = useState<'insights' | 'activity'>('insights');
  return <div className="h-full flex flex-col" data-id="element-136">
      {/* Header */}
      <div className="flex justify-between items-center mb-6" data-id="element-137">
        <div className="flex items-center" data-id="element-138">
          <Button variant="ghost" size="sm" onClick={onBack} icon={<ArrowLeft size={16} data-id="element-140" />} data-id="element-139">
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 ml-2" data-id="element-141">
            {workspace.name}
          </h1>
        </div>
        <div className="flex items-center space-x-2" data-id="element-142">
          <Button variant="outline" size="sm" icon={<Users size={16} data-id="element-144" />} data-id="element-143">
            Invite
          </Button>
          <Button variant="outline" size="sm" icon={<Download size={16} data-id="element-146" />} data-id="element-145">
            Export
          </Button>
          <Button variant="ghost" size="sm" icon={<MoreHorizontal size={16} data-id="element-148" />} data-id="element-147">
            More
          </Button>
        </div>
      </div>
      {/* Workspace Info */}
      <div className="bg-white rounded-lg shadow p-4 mb-6" data-id="element-149">
        <div className="flex justify-between items-start" data-id="element-150">
          <div data-id="element-151">
            <p className="text-gray-500" data-id="element-152">{workspace.description}</p>
            <div className="mt-4 flex items-center space-x-6" data-id="element-153">
              <div className="flex items-center text-sm text-gray-500" data-id="element-154">
                <Clock size={16} className="mr-1" data-id="element-155" />
                <span data-id="element-156">Updated {workspace.lastUpdated}</span>
              </div>
              <div className="flex items-center text-sm text-gray-500" data-id="element-157">
                <Badge variant={workspace.status === 'Active' ? 'success' : 'default'} data-id="element-158">
                  {workspace.status}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center" data-id="element-159">
            <div className="flex -space-x-2 mr-2" data-id="element-160">
              {workspace.collaborators.slice(0, 5).map((user, index) => <Avatar key={index} name={user.name} src={user.avatar} size="sm" data-id="element-161" />)}
              {workspace.collaborators.length > 5 && <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-600" data-id="element-162">
                  +{workspace.collaborators.length - 5}
                </div>}
            </div>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6" data-id="element-163">
        <div className="border-b border-gray-200" data-id="element-164">
          <nav className="flex -mb-px" data-id="element-165">
            <button onClick={() => setActiveTab('insights')} className={`py-4 px-6 font-medium text-sm border-b-2 ${activeTab === 'insights' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-166">
              Insights
            </button>
            <button onClick={() => setActiveTab('activity')} className={`py-4 px-6 font-medium text-sm border-b-2 ${activeTab === 'activity' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-167">
              Activity
            </button>
          </nav>
        </div>
        {/* Tab Content */}
        <div className="p-4" data-id="element-168">
          {activeTab === 'insights' ? <div className="space-y-4" data-id="element-169">
              <div className="flex justify-between items-center" data-id="element-170">
                <h3 className="text-lg font-medium" data-id="element-171">Insights</h3>
                <Button size="sm" icon={<Plus size={16} data-id="element-173" />} data-id="element-172">
                  Add Insight
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-174">
                {mockInsights.map(insight => <InsightCard key={insight.id} insight={insight} data-id="element-175" />)}
              </div>
              {mockInsights.length === 0 && <div className="text-center py-8" data-id="element-176">
                  <p className="text-gray-500" data-id="element-177">No insights added yet</p>
                  <p className="text-gray-500 text-sm mt-1" data-id="element-178">
                    Add insights to start collaborating
                  </p>
                </div>}
            </div> : <ActivityFeed activities={workspace.activities} data-id="element-179" />}
        </div>
      </div>
      {/* Collaboration Summary */}
      <div className="bg-white rounded-lg shadow p-4" data-id="element-180">
        <h3 className="text-lg font-medium mb-4" data-id="element-181">Collaboration Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-182">
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-183">
            <div className="flex items-center text-gray-500 mb-2" data-id="element-184">
              <MessageSquare size={16} className="mr-2" data-id="element-185" />
              <span className="font-medium" data-id="element-186">Comments</span>
            </div>
            <p className="text-2xl font-semibold" data-id="element-187">{workspace.stats.comments}</p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-188">
            <div className="flex items-center text-gray-500 mb-2" data-id="element-189">
              <CheckSquare size={16} className="mr-2" data-id="element-190" />
              <span className="font-medium" data-id="element-191">Actions</span>
            </div>
            <p className="text-2xl font-semibold" data-id="element-192">
              {workspace.stats.actions}
              <span className="text-sm text-gray-500 font-normal ml-2" data-id="element-193">
                ({workspace.stats.completedActions} completed)
              </span>
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-194">
            <div className="flex items-center text-gray-500 mb-2" data-id="element-195">
              <Users size={16} className="mr-2" data-id="element-196" />
              <span className="font-medium" data-id="element-197">Contributors</span>
            </div>
            <p className="text-2xl font-semibold" data-id="element-198">
              {workspace.collaborators.length}
            </p>
          </div>
        </div>
      </div>
    </div>;
}